/**
 * สไลด์, the ground slide (data/slide.ts): Ctrl + a direction. The player skids along the ground, setting off at
 * once and slowing under a steady friction; a move or a jump cuts it short in its end window (`SLIDE.cancelAt`).
 *
 * It moves the player through the World's own carried-movement fields (`dashT`, `dashVX`, `dashVY`: the World moves
 * a dashing player by them before the walk and skips the walk), set afresh every tick from the slide's distance
 * curve, so the ground covered is exact and walls stop it as they stop a dash. Before the end window `dashT` covers
 * the rest of the slide, so moves, guards and dodges wait; in the window it is one tick only, so a command between
 * ticks is taken (and the slide ends when a move, a jump or a dodge starts).
 *
 * Deterministic: time only from `playerStep`'s dt; no randomness.
 */
import { SLIDE, slideDistAt } from '../data/slide';
import type { Entity } from '../world';
import type { SystemHost, WorldSystem } from './types';

/** Commands (part of `PlayerCommand`): slide toward (dx, dy) (World axes; zero: the way the player faces). */
export type SlideCommand = { t: 'slide'; dx: number; dy: number };
/** Events (part of `WorldEvent`): a slide set off (its unit direction). */
export type SlideEvent = { type: 'slide'; id: number; dx: number; dy: number };

/** A slide's own numbers: `SLIDE`'s, or a rule set's (`setSlideTuner`: a duel bout, systems/duelSlide.ts). */
export interface SlideTune {
  dist: number;
  time: number;
  stamina: number;
  cancelAt: number;
}
/** Changes a slide's numbers as it sets off (null: `SLIDE`'s). Called in the slider's command slot. */
export type SlideTuner = (p: Entity, dx: number, dy: number) => Partial<SlideTune> | null;
const TUNERS = new WeakMap<object, SlideTuner>();
/** Sets (or clears) the World's slide tuner. */
export const setSlideTuner = (w: object, f: SlideTuner | null): void => {
  if (f) TUNERS.set(w, f);
  else TUNERS.delete(w);
};

/** A slide under way, as the renderer reads it (`slideOf`). */
export interface SlideState {
  /** Seconds since it set off, and how long it lasts. */
  t: number;
  readonly time: number;
  /** Metres it covers in all, and the share of it from which it can be cut short. */
  readonly len: number;
  readonly cancelAt: number;
  /** Unit direction (World axes). */
  readonly dx: number;
  readonly dy: number;
  /** Metres covered so far. */
  dist: number;
  /** The velocity last handed to the World (to tell a dodge or a move that took the fields over). */
  vx: number;
  vy: number;
  /** Where the entity stood before the last step, and how far that step should have gone. */
  px: number;
  py: number;
  want: number;
}

const SLIDES = new WeakMap<Entity, SlideState>();
const COOL = new WeakMap<Entity, number>();

/** The slide `e` is in, or null (read-only for the renderer). */
export const slideOf = (e: Entity): Readonly<SlideState> | null => SLIDES.get(e) ?? null;

export class SlideSystem implements WorldSystem {
  readonly name = 'slide';

  constructor(private readonly h: SystemHost) {}

  /** A jump before the end window is refused (it cuts the slide only at its end). */
  refuse(cmd: { t: string }): boolean {
    if (cmd.t !== 'jump') return false;
    const s = SLIDES.get(this.h.world.player);
    return !!s && s.t < s.cancelAt * s.time;
  }

  readonly commands = {
    slide: (cmd: SlideCommand) => {
      const p = this.h.world.player;
      if (p.dead || p.stunT > 0 || p.cast || p.dashT > 0 || p.spinT > 0 || p.swingT > 0 || p.z > 0 || p.mount) return;
      if (SLIDES.has(p) || (COOL.get(p) ?? 0) > 0) return;
      const l = Math.hypot(cmd.dx, cmd.dy);
      const fl = Math.hypot(p.dirX, p.dirY) || 1;
      const [dx, dy] = l > 1e-6 ? [cmd.dx / l, cmd.dy / l] : [p.dirX / fl, p.dirY / fl];
      const tune: SlideTune = { dist: SLIDE.dist, time: SLIDE.time, stamina: SLIDE.stamina, cancelAt: SLIDE.cancelAt };
      Object.assign(tune, TUNERS.get(this.h.world)?.(p, dx, dy) ?? {});
      if (p.stamina < tune.stamina) {
        this.h.notice('พลังกายไม่พอ');
        return;
      }
      p.stamina -= tune.stamina;
      p.blocking = false;
      p.path = null;
      this.h.stopMeditating();
      SLIDES.set(p, {
        t: 0,
        time: tune.time,
        len: tune.dist,
        cancelAt: tune.cancelAt,
        dx,
        dy,
        dist: 0,
        vx: 0,
        vy: 0,
        px: p.x,
        py: p.y,
        want: 0,
      });
      // Carried from this tick on (the World moves it before the walk).
      p.dashT = tune.time;
      p.dashVX = 0;
      p.dashVY = 0;
      this.h.emit({ type: 'slide', id: p.id, dx, dy });
    },
  };

  playerStep(dt: number): void {
    const p = this.h.world.player;
    const cool = COOL.get(p);
    if (cool !== undefined) {
      if (cool - dt <= 0) COOL.delete(p);
      else COOL.set(p, cool - dt);
    }
    const s = SLIDES.get(p);
    if (!s) return;
    // Over: the World or the player took over (a move, a jump, a dodge, a stun), or a wall stopped it.
    const taken = (s.t > 0 && (p.dashVX !== s.vx || p.dashVY !== s.vy)) || p.cast || p.swingT > 0 || p.spinT > 0;
    const stopped = p.dead || p.stunT > 0 || p.z > 0 || p.vz > 0 || !!p.mount;
    const blocked = s.want > 1e-3 && Math.hypot(p.x - s.px, p.y - s.py) < s.want * SLIDE.blocked;
    if (taken || stopped || blocked || s.t >= s.time) return this.end(p, !taken);
    const s1 = Math.min(1, (s.t + dt) / s.time);
    const d1 = slideDistAt(s1, s.len);
    const step = d1 - s.dist;
    s.vx = dt > 0 ? (s.dx * step) / dt : 0;
    s.vy = dt > 0 ? (s.dy * step) / dt : 0;
    s.px = p.x;
    s.py = p.y;
    s.want = step;
    s.dist = d1;
    s.t += dt;
    p.dashVX = s.vx;
    p.dashVY = s.vy;
    // Before the end window the rest of the slide holds other commands off; in it, one tick at a time.
    p.dashT = s.t < s.cancelAt * s.time ? s.time - s.t + dt : dt;
  }

  private end(p: Entity, release: boolean): void {
    SLIDES.delete(p);
    COOL.set(p, SLIDE.cooldown);
    if (release) {
      p.dashT = 0;
      p.dashVX = 0;
      p.dashVY = 0;
    }
  }
}
